export interface Person {
  personId: string;
  name: string;
  role: string;
  email?: string;
}

/** One entry in a line item's change log — every status change or approve/unapprove action,
 *  not just the most recent one. `by` is a Nimbus People-register personId, the same
 *  namespace `approvedBy` below and `ProjectedEndDateException.approvedBy` already use. */
export interface HistoryEntry {
  id: string;
  at: string;
  by: string;
  action: string;
}

export interface FunctionalRequirement {
  id: string;
  text: string;
  status: string;
  /** Whether it's approved *right now*, and by whom — kept alongside `history` (the full
   *  log) because it's the fast check the row's trigger label reads without having to scan
   *  the log. Cleared the moment status moves off Approved (nullable rather than just
   *  optional, for exactly that reason) — "was this ever approved, and by whom" is what
   *  `history` answers instead, so this field only ever means "approved right now". */
  approvedBy?: string | null;
  approvedAt?: string | null;
  /** Every status change, newest last. Requirements log one entry per status change (not
   *  just transitions to Approved) — the point of this field is a full log, not just the
   *  latest approval. */
  history?: HistoryEntry[];
}

export interface Milestone {
  id: string;
  name: string;
  targetDate: string;
  status: 'Upcoming' | 'Done' | 'Missed' | 'At Risk';
  actualDate?: string;
  /** Weight (%) this milestone contributes to the initiative's overall completion. */
  weight?: number;
  /** How far along this specific milestone is (0-100). Falls back to 100 for Done / 0 otherwise if unset. */
  completionPercent?: number;
  /** Independent of status — a milestone can be marked Done without anyone having signed
   *  off on it yet. Same personId/timestamp convention as FunctionalRequirement.approvedBy. */
  approvedBy?: string | null;
  approvedAt?: string | null;
  /** Every approve/unapprove action, newest last. Does NOT capture status changes made via
   *  weekly report submission (a different flow entirely, merged server-side in
   *  inittrack-api's _apply_milestone_updates) — only what happens on this card. */
  history?: HistoryEntry[];
}

export interface Stakeholder {
  name: string;
  role: string;
}

export interface Risk {
  id: string;
  description: string;
  severity: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Open' | 'Mitigated' | 'Closed';
  owner?: string;
}

/** An action added directly on an initiative's own Actions tab - distinct from an action
 *  raised in a meeting (a MinuteEntry with category 'action'), which still shows up on the
 *  same tab but lives on its meeting, not the initiative. */
export interface Action {
  id: string;
  description: string;
  ownerId?: string;
  dueDate?: string;
  status: 'Open' | 'Done';
}

/** A decision recorded directly on an initiative's own Decisions tab - same relationship to
 *  meeting-minutes decisions as Action above. */
export interface Decision {
  id: string;
  description: string;
  decidedBy?: string;
}

export type Health = 'On Track' | 'At Risk' | 'Blocked' | 'Not Started';
export type InitiativeStatus = 'Not Started' | 'In Progress' | 'Blocked' | 'On Hold' | 'Completed' | 'Cancelled';
export type Priority = 'Low' | 'Medium' | 'High' | 'Critical';

/** Portfolio management hierarchy: a Portfolio contains Programs, a Program contains
 *  Projects. `''` covers every initiative recorded before this field existed. */
export type InitiativeTier = 'Portfolio' | 'Program' | 'Project' | '';
export const INITIATIVE_TIERS: InitiativeTier[] = ['Portfolio', 'Program', 'Project'];
/** The tier immediately above each tier, for validating parentInitiativeId choices - a
 *  Program's parent must be a Portfolio, a Project's must be a Program. Portfolio has none. */
export const PARENT_TIER_FOR: Record<'Program' | 'Project', 'Portfolio' | 'Program'> = {
  Program: 'Portfolio',
  Project: 'Program',
};

/** A request to change the projected end date after it's already been set once — held
 * as Pending until an approver signs off; the initiative's currentProjectedEndDate only
 * changes once Approved. */
export interface ProjectedEndDateException {
  id: string;
  requestedBy: string;
  requestedAt: string;
  previousProjectedEndDate: string;
  newProjectedEndDate: string;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  reportId?: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
}

export interface Initiative {
  initiativeId: string;
  name: string;
  summary: string;
  description: string;
  /** What exists today - researched before deciding desiredSituation, so Scope below can be
   *  set accurately against the gap between the two. Quill HTML, same shape as description. */
  currentSituation: string;
  /** What should exist once this initiative is done. */
  desiredSituation: string;
  category: string;
  ownerId: string;
  contributorIds: string[];
  status: InitiativeStatus;
  priority: Priority;
  startDate: string;
  originalTargetEndDate: string;
  currentProjectedEndDate: string;
  /** Set while a date-change exception is awaiting approval; cleared once resolved. */
  pendingProjectedEndDate?: string;
  exceptions: ProjectedEndDateException[];
  percentComplete: number;
  health: Health;
  functionalRequirements: FunctionalRequirement[];
  nonFunctionalRequirements: FunctionalRequirement[];
  /**
   * The scope boundary. Same shape as the requirement lists so the list editor and row
   * renderer are shared rather than duplicated.
   *
   * `outOfScope` earns its keep more than `inScope` does: it is the record of what somebody
   * asked for and was deliberately excluded, which is the thing that gets disputed six
   * months in. Optional because records created before this field existed will not have it.
   */
  inScope?: FunctionalRequirement[];
  outOfScope?: FunctionalRequirement[];
  milestones: Milestone[];
  stakeholders: Stakeholder[];
  risks: Risk[];
  /** Optional because records created before these fields existed will not have them - same
   *  convention as inScope/outOfScope above. */
  actions?: Action[];
  decisions?: Decision[];
  links: string[];
  targetMetricIds: string[];
  independentVariables: string[];
  /** Ongoing/standing work with no real end date (e.g. a recurring stand-up), as opposed to a
   *  time-bound project. Distinct from Metric.trackedAsBau (a Service Catalog metric that
   *  outlives the project that created it) - this is about the initiative itself never having
   *  a completion. Optional because records created before this field existed will not have it. */
  isBau?: boolean;
  /** Portfolio/Program/Project tier and the parent this initiative rolls up into - '' /
   *  unset for every initiative recorded before this hierarchy existed, which the tree and
   *  Gantt views both treat as an unparented root rather than hiding it. */
  tier?: InitiativeTier;
  parentInitiativeId?: string;
  /** The system-managed "Total Annual Spend" Metric this initiative owns, auto-created the
   *  first time any Asset gets linked via Linked Artefacts (see InitiativeDetailPage.tsx) - its
   *  id is stored here so it's found by reference, not by matching on name. Its readings are
   *  kept approximately in sync (written whenever someone views this initiative's Overview tab
   *  and the computed total has changed since the last recorded reading), not on every
   *  underlying change - there's no event-driven trigger for "a linked asset's billing changed
   *  elsewhere" today. */
  assetSpendMetricId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyReport {
  reportId: string;
  initiativeId: string;
  weekOf: string;
  submittedBy: string;
  percentComplete: number;
  health: Health;
  summary: string;
  blockers: string;
  nextSteps: string;
  projectedEndDate: string;
  /** Per-milestone status/completion set on this report — the primary source of truth for
   * percentComplete on initiatives with weighted milestones (see milestoneCompletion()).
   * evidenceNote is an optional, reporter-supplied proof-of-completion line, only ever shown
   * when a status moves to Done - never required, never a substitute for approvedBy/history. */
  milestoneUpdates: { milestoneId: string; status: Milestone['status']; completionPercent: number; evidenceNote?: string }[];
  /** Same idea as milestoneUpdates, for functional/non-functional requirements - status only,
   *  never approvedBy/history, which stay a separate lane owned by the Overview tab. Optional
   *  because reports created before this field existed won't have it. evidenceNote is the same
   *  optional proof-of-completion line as milestoneUpdates', shown when status moves to Done. */
  requirementUpdates?: { requirementId: string; status: string; evidenceNote?: string }[];
  /** What the reporter said they'd move next, tagged from this report's still-open milestones/
   *  requirements (or, for a Programme/Portfolio report, still-open child initiatives) at submit
   *  time - the next report reads this back to ask "did you?" instead of trusting anyone's
   *  memory of a free-text paragraph. */
  committedNext?: { kind: 'milestone' | 'requirement' | 'initiative'; id: string; label: string }[];
  metricReadings: { metricId: string; value: number; note?: string }[];
  createdAt: string;
}

export interface Domain {
  domainId: string;
  name: string;
  description: string;
}

export interface Service {
  serviceId: string;
  domainId: string;
  name: string;
  description: string;
  ownerId: string;
}

export interface Metric {
  metricId: string;
  serviceId: string;
  name: string;
  description: string;
  unit: string;
  direction: 'higher' | 'lower';
  cadence: string;
  target?: number;
  baseline?: number;
  origin: 'Pre-existing' | 'Project';
  originInitiativeId?: string;
  bauOwnerId?: string;
  trackedAsBau: boolean;
  /** Same fixed scale as Asset.criticality (constants/tone.ts's SEVERITY_TONE) - weights how
   *  much this metric drags down a Program/Portfolio's Operational Health when off-target, and
   *  whether it forces a severity floor. Optional since legacy rows predate the field -
   *  read sites fall back to 'Medium', same convention as AssetsPage.tsx's own criticality chip. */
  criticality?: 'Critical' | 'High' | 'Medium' | 'Low';
}

export interface MetricReading {
  readingId: string;
  metricId: string;
  date: string;
  value: number;
  note?: string;
  source: 'manual' | 'report';
  sourceReportId?: string;
  sourceInitiativeId?: string;
}
